// Per-user PAGE DEFAULTS (Robert 2026-07-22): the filter values a page opens
// with on FIRST login / a new browser / cleared cache. These are the LAST
// resort — a page uses them ONLY when it has no saved session state
// (localStorage / sessionStorage). In-session changes and saved state always
// win, so a user who tweaks a filter keeps their tweak; the defaults just seed
// the very first, blank-slate visit.
//
// The signed-in user's own object (id, flags, page_defaults) is cached here at
// boot by App.jsx so the pages' synchronous initial-state functions can read
// it without prop-drilling through every report wrapper.
//
// The "me" sentinel, two flags (users.md — they are different):
//  * BASELINE "me" (nothing configured) applies only to REAL PCOs
//    (is_project_coordinator — preserves the 2026-07-16 landing rule; office
//    staff who merely appear in dropdowns still land on All).
//  * An EXPLICITLY configured "me" is the user's own choice — honoured for
//    anyone in the PCO list (shown_in_project_coordinator; outside the list
//    the Search dropdown couldn't even display the value, so it's All).

let sessionUser = null;

// Read-only access to the signed-in user for components that sit too deep to be
// handed it as a prop (the claim tabs).
export function getSessionUser() {
  return sessionUser;
}

export function setSessionUser(user) {
  sessionUser = user || null;
}

const isRealPco = () => !!sessionUser?.is_project_coordinator;
const inPcoList = () => !!sessionUser?.shown_in_project_coordinator;

// Built-in baseline: a real PCO lands on THEIR OWN jobs; sort leads with what
// needs attention; RDR = Only-With-Records + Detail (the shipped defaults).
// `more` = does the extra-filters row open expanded? Default NO: it is behind
// the More options button for everyone, because Karen Sweatman is the only
// person who has asked to see it (Robert 2026-08-17), and the row costs a
// chunk of the job list on a 1080p screen. Hers is one setting rather than a
// layout everyone else pays for.
const SEARCH_BASE = { pco: "me", sort: "attention", more: "default" };
const RDR_BASE = { pm: "me", division: "", withRecordsOnly: true, mode: "detail" };

// May a "me"-flavoured value apply? explicit = the user picked it themselves.
function meAllowed(explicit) {
  return explicit ? inPcoList() : isRealPco() && inPcoList();
}

// The Search page's initial filters when no saved search exists. Resolves the
// "me" sentinel to the user's id (or All where it doesn't apply).
export function searchDefaults(emptyFilters) {
  const stored = sessionUser?.page_defaults?.search || {};
  const cfg = { ...SEARCH_BASE, ...stored };
  let pco = cfg.pco;
  if (pco === "me") pco = meAllowed("pco" in stored) ? String(sessionUser.id) : "";
  const sort = cfg.sort === "newest" ? "newest" : "attention";
  return { ...emptyFilters, project_coordinator_id: pco, sort };
}

// Should the Search page's extra-filters row start OPEN? Unlike the filter
// values above this is not a last-resort seed — the expander state is not saved
// per session, so there is no remembered choice for it to lose to. It is a
// standing preference: set it once and the row is always there. Collapsing it
// with the Less options button still works for that visit.
export function searchMoreOpenDefault() {
  return (sessionUser?.page_defaults?.search?.more ?? SEARCH_BASE.more) === "all";
}

// One RDR filter's default (pm / division / withRecordsOnly / mode), used only
// when the section has no saved session state. The pm "me"/"me+" sentinels are
// first-class filter values the backend resolves; where "me" doesn't apply the
// section opens on All (the legacy default radio).
export function rdrDefault(key) {
  const stored = sessionUser?.page_defaults?.rdr || {};
  const cfg = { ...RDR_BASE, ...stored };
  const val = cfg[key];
  if (key === "pm" && (val === "me" || val === "me+") && !meAllowed("pm" in stored)) return "";
  return val;
}

// Saving new defaults must WIN immediately (Robert 2026-07-22): the remembered
// filters for the affected areas are purged, so the next visit seeds from the
// new defaults instead of yesterday's selections. After that, in-session
// changes are remembered again as normal. Prefix matching keeps this immune to
// storage-key version bumps (claimSearch:last:v3 → v4 etc.).
export function clearSavedPageState() {
  if (typeof window === "undefined") return;
  const purge = (store, prefix) => {
    try {
      const doomed = [];
      for (let i = 0; i < store.length; i++) {
        const k = store.key(i);
        if (k && k.startsWith(prefix)) doomed.push(k);
      }
      doomed.forEach((k) => store.removeItem(k));
    } catch {
      /* private mode etc. */
    }
  };
  // The remembered search (filters/page) — sessionStorage since v6 (per tab;
  // only THIS tab can be purged, other open tabs keep their in-flight search
  // until closed, which is the per-tab model working as intended). The
  // localStorage purge stays for retired pre-v6 blobs.
  purge(window.sessionStorage, "claimSearch:last");
  purge(window.localStorage, "claimSearch:last");
  purge(window.sessionStorage, "rdrReport:"); // every RDR section's filters + scroll
}

// What the edit panel shows: stored values over the EFFECTIVE baseline for
// this user (a non-PCO's unset coordinator reads All, matching what actually
// happens — never a phantom "My jobs").
export function currentPageDefaults() {
  const pd = sessionUser?.page_defaults || {};
  const base = isRealPco() && inPcoList()
    ? { search: SEARCH_BASE, rdr: RDR_BASE }
    : { search: { ...SEARCH_BASE, pco: "" }, rdr: { ...RDR_BASE, pm: "" } };
  return {
    search: { ...base.search, ...(pd.search || {}) },
    rdr: { ...base.rdr, ...(pd.rdr || {}) },
  };
}
